'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import Formatted from '@flipster/react/design-utils/formatted';
import ArrowDownRightIcon from '@flipster/react/icon/generated/ArrowDownRightIcon';
import ArrowUpRightIcon from '@flipster/react/icon/generated/ArrowUpRightIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, vstack } from '@flipster/styled-system/patterns';

import { CurrencyDisplay } from '../../../../misc/display';
import { btcPriceCardBunja } from './btc-price-card.state';
import { btcPricePrecision } from './state';

export const BtcPriceCard = () => {
  const { startingPriceAtom, startingPriceDateAtom, priceInfoAtom } = useBunja(btcPriceCardBunja);

  const startingPrice = useAtomValue(startingPriceAtom);
  const startingPriceDate = useAtomValue(startingPriceDateAtom);
  const { currentPrice, priceDiff, priceChangePercent, isPositive } = useAtomValue(priceInfoAtom);

  return (
    <div
      className={vstack({
        w: { base: '100%', tablet: 360 },
        flexShrink: 0,
        p: 24,
        gap: 24,
        rounded: 16,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'border.default.level-2',
        bg: 'background.default.level-0',
        alignItems: 'flex-start',
      })}
    >
      <div className={hstack({ gap: 8, alignItems: 'center' })}>
        <CurrencyDisplay
          currency='BTC'
          icon={<CurrencyDisplay.Icon size={20} space={8} />}
          mainLabel={
            <CurrencyDisplay.Label css={{ textStyle: 'Title/S', color: 'content.default.level-5' }}>
              <FormattedMessage id='Campaign.r260123.Symbol_price' values={{ symbol: 'BTC' }} />
            </CurrencyDisplay.Label>
          }
        />
      </div>

      <div
        className={vstack({
          gap: 8,
          p: 16,
          w: '100%',
          rounded: 8,
          bg: 'background.accent.secondary.tint',
          alignItems: 'center',
          justifyContent: 'center',
        })}
      >
        <span className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-3' })}>
          <FormattedMessage id='Campaign.r260123.Starting_price' />
        </span>
        <span className={css({ textStyle: 'Display/L', color: 'content.default.level-5' })}>
          {startingPrice ? <Formatted.Number value={startingPrice} /> : '-'}
        </span>
        <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-1' })}>
          {startingPriceDate}
        </span>
      </div>

      <div className={vstack({ gap: 16, w: '100%', alignItems: 'flex-start' })}>
        <div
          className={vstack({
            gap: 8,
            w: '100%',
            pb: 16,
            borderBottomWidth: '1px',
            borderBottomStyle: 'solid',
            borderColor: 'border.default.level-2',
            alignItems: 'flex-start',
          })}
        >
          <span className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-3' })}>
            <FormattedMessage id='Assets.r250401.Current_price' />
          </span>
          <div className={hstack({ gap: 8, alignItems: 'center' })}>
            <span className={css({ textStyle: 'Title/M', color: 'content.default.level-5' })}>
              <Formatted.Number value={currentPrice} />
            </span>
            {priceChangePercent !== null && (
              <div className={hstack({ gap: 2, alignItems: 'center' })}>
                {isPositive ? (
                  <ArrowUpRightIcon size={16} className={css({ color: 'content.state.positive' })} />
                ) : (
                  <ArrowDownRightIcon size={16} className={css({ color: 'content.state.negative' })} />
                )}
                <Formatted.Percent
                  value={priceChangePercent}
                  precision={btcPricePrecision}
                  toFixed
                  isRate
                  css={{
                    textStyle: 'Title/XS',
                    color: isPositive ? 'content.state.positive' : 'content.state.negative',
                  }}
                />
              </div>
            )}
          </div>
        </div>

        <div className={vstack({ gap: 4, alignItems: 'flex-start' })}>
          <span
            className={css({
              textStyle: 'Title/M/Monospace',
              color:
                priceDiff === null
                  ? 'content.default.level-2'
                  : isPositive
                    ? 'content.state.positive'
                    : 'content.state.negative',
            })}
          >
            {priceDiff === null ? (
              '-'
            ) : Number(priceDiff) > 0 ? (
              <FormattedMessage id='Campaign.r260123.Amount_higher' values={{ amount: Math.abs(Number(priceDiff)) }} />
            ) : Number(priceDiff) < 0 ? (
              <FormattedMessage id='Campaign.r260123.Amount_lower' values={{ amount: Math.abs(Number(priceDiff)) }} />
            ) : (
              <FormattedMessage id='Campaign.r260123.No_change' />
            )}
          </span>
          <span className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-2' })}>
            <FormattedMessage id='Campaign.r260123.Than_the_starting_price' values={{ price: startingPrice ?? '-' }} />
          </span>
        </div>
      </div>
    </div>
  );
};
